import type { ButtonHTMLAttributes, CSSProperties, ReactNode } from 'react'

export function AppShell({ title, subtitle, primaryColor, children }: { title: string; subtitle: string; primaryColor: string; children: ReactNode }) {
  const style = { ['--lk-primary' as string]: primaryColor } as CSSProperties

  return (
    <div
      className="min-h-screen p-6 text-slate-900"
      style={{
        ...style,
        background: 'radial-gradient(circle at top left, color-mix(in srgb, var(--lk-primary) 18%, white), transparent 30%), linear-gradient(180deg, #f6fbff 0%, #f4f8fc 100%)',
      }}
    >
      <header className="mb-5 rounded-[28px] border border-slate-200/70 bg-white/90 p-6 shadow-[0_14px_40px_rgba(15,23,42,0.06)] backdrop-blur">
        <div>
          <p className="mb-2 text-xs font-bold uppercase tracking-[0.14em] text-[var(--lk-primary)]">Unified LK Platform</p>
          <h1 className="text-[clamp(28px,4vw,40px)] font-semibold leading-tight">{title}</h1>
          <p className="mt-2 text-sm leading-6 text-slate-500">{subtitle}</p>
        </div>
      </header>
      <main className="grid gap-5 [grid-template-columns:repeat(auto-fit,minmax(280px,1fr))]">{children}</main>
    </div>
  )
}

export function Card({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section className="grid gap-4 rounded-[28px] border border-slate-200/70 bg-white/95 p-5 shadow-[0_14px_40px_rgba(15,23,42,0.06)]">
      <h2 className="text-xl font-semibold">{title}</h2>
      {children}
    </section>
  )
}

type ButtonVariant = 'primary' | 'secondary'

export function Button({
  children,
  className = '',
  variant = 'primary',
  ...props
}: { children: ReactNode; className?: string; variant?: ButtonVariant } & ButtonHTMLAttributes<HTMLButtonElement>) {
  const variantClassName = variant === 'secondary'
    ? 'border border-slate-200 bg-slate-100 text-slate-800 shadow-none hover:bg-slate-200'
    : 'border-0 bg-[linear-gradient(135deg,var(--lk-primary,#0099ff)_0%,color-mix(in_srgb,var(--lk-primary,#0099ff)_78%,black)_100%)] text-white shadow-[0_10px_22px_color-mix(in_srgb,var(--lk-primary,#0099ff)_22%,transparent)] hover:brightness-105'

  return (
    <button
      className={`inline-flex items-center justify-center rounded-[18px] px-4 py-3 font-medium transition disabled:cursor-progress disabled:opacity-65 ${variantClassName} ${className}`.trim()}
      {...props}
    >
      {children}
    </button>
  )
}
